import API_BASE from '../config/api';

class ApiService {
  constructor() {
    this.authToken = localStorage.getItem('premier_token') || null;
  }

  setAuthToken(token) {
    this.authToken = token;
    if (token) localStorage.setItem('premier_token', token);
    else localStorage.removeItem('premier_token');
  }

  clearAuthToken() {
    this.authToken = null;
    localStorage.removeItem('premier_token');
  }

  getHeaders() {
    const headers = { 'Content-Type': 'application/json' };
    if (this.authToken) headers['Authorization'] = `Bearer ${this.authToken}`;
    return headers;
  }

  async request(method, endpoint, body) {
    const options = { method, headers: this.getHeaders() };
    if (body) options.body = JSON.stringify(body);
    const res = await fetch(`${API_BASE}${endpoint}`, options);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
    return data;
  }

  get(endpoint)              { return this.request('GET', endpoint); }
  post(endpoint, body)       { return this.request('POST', endpoint, body); }
  put(endpoint, body)        { return this.request('PUT', endpoint, body); }
  delete(endpoint)           { return this.request('DELETE', endpoint); }

  // Auth
  async login(email, password) {
    const data = await this.post('/api/auth/login', { email, password });
    if (data.token) this.setAuthToken(data.token);
    return data;
  }
  async logout() {
    try { await this.post('/api/auth/logout'); } catch { /* ignore */ }
    this.clearAuthToken();
  }
  verifyToken()                        { return this.get('/api/auth/verify'); }
  changePassword(currentPassword, newPassword) {
    return this.put('/api/auth/change-password', { currentPassword, newPassword });
  }

  // Inventory
  getInventory()                       { return this.get('/api/inventory'); }
  createItem(data)                     { return this.post('/api/inventory', data); }
  updateItem(id, data)                 { return this.put(`/api/inventory/${id}`, data); }
  deleteItem(id)                       { return this.delete(`/api/inventory/${id}`); }
  adjustStock(id, change, reason)      { return this.put(`/api/inventory/${id}/stock`, { change, reason }); }
  uploadItemImage(id, file) {
    const formData = new FormData();
    formData.append('image', file);
    return fetch(`${API_BASE}/api/inventory/${id}/image`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${this.authToken}` },
      body: formData,
    }).then(async r => { const d = await r.json(); if (!r.ok) throw new Error(d.error || 'Upload failed'); return d; });
  }
  deleteItemImage(id) { return this.delete(`/api/inventory/${id}/image`); }

  // Transactions
  getTransactions(filters = {}) {
    const params = new URLSearchParams(filters).toString();
    return this.get(`/api/transactions${params ? '?' + params : ''}`);
  }
  createTransaction(data)              { return this.post('/api/transactions', data); }
  updateTransaction(id, data)          { return this.put(`/api/transactions/${id}`, data); }
  deleteTransaction(id)                { return this.delete(`/api/transactions/${id}`); }

  // Recurring expenses (overhead)
  getRecurring()                        { return this.get('/api/recurring'); }
  createRecurring(data)                 { return this.post('/api/recurring', data); }
  updateRecurring(id, data)             { return this.put(`/api/recurring/${id}`, data); }
  deleteRecurring(id)                   { return this.delete(`/api/recurring/${id}`); }

  // Suppliers
  getSuppliers()                       { return this.get('/api/suppliers'); }
  createSupplier(data)                 { return this.post('/api/suppliers', data); }
  updateSupplier(id, data)             { return this.put(`/api/suppliers/${id}`, data); }
  deleteSupplier(id)                   { return this.delete(`/api/suppliers/${id}`); }

  // Users
  getUsers()                           { return this.get('/api/users'); }
  createUser(data)                     { return this.post('/api/users', data); }
  updateUser(id, data)                 { return this.put(`/api/users/${id}`, data); }
  deleteUser(id)                       { return this.delete(`/api/users/${id}`); }

  // Shipments
  getShipments()                       { return this.get('/api/shipments'); }
  createShipment(data)                 { return this.post('/api/shipments', data); }
  updateShipment(id, data)             { return this.put(`/api/shipments/${id}`, data); }
  deleteShipment(id)                   { return this.delete(`/api/shipments/${id}`); }

  // Shipment Items
  getShipmentItems(shipmentId)         { return this.get(`/api/shipment-items${shipmentId ? '?shipmentId=' + shipmentId : ''}`); }
  createShipmentItem(data)             { return this.post('/api/shipment-items', data); }
  updateShipmentItem(id, data)         { return this.put(`/api/shipment-items/${id}`, data); }
  deleteShipmentItem(id)               { return this.delete(`/api/shipment-items/${id}`); }

  // Pricing Scenarios
  getPricingScenarios(itemId)          { return this.get(`/api/pricing-scenarios${itemId ? '?itemId=' + itemId : ''}`); }
  createPricingScenario(data)          { return this.post('/api/pricing-scenarios', data); }
  updatePricingScenario(id, data)      { return this.put(`/api/pricing-scenarios/${id}`, data); }
  deletePricingScenario(id)            { return this.delete(`/api/pricing-scenarios/${id}`); }

  // Settings
  getSettings()                        { return this.get('/api/settings'); }
  updateSettings(data)                 { return this.put('/api/settings', data); }

  // Audit
  getAudit()                           { return this.get('/api/audit'); }
}

const apiService = new ApiService();
export default apiService;
